import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Activity, Check } from 'lucide-react';
import { useOSPulse } from '../hooks/useOSPulse';
import { useT } from '../i18n';

/* AIActivityTimeline — real Event Bus timeline ("17:42 ✓ Penny published…").
   Data from /api/pulse events (real system activity, titles only). */

const AGENT_BY_TYPE = {
  ARTICLE_PUBLISHED: 'Penny',
  SOCIAL_PACKAGE_READY: 'Emergent',
  RSS_UPDATED: 'Emergent',
  POST_PUBLISHED: 'Sonny',
  CAMPAIGN_CREATED: 'Sonny',
  NEWSLETTER_SUBSCRIBED: 'Eva',
  LEAD_CREATED: 'Stan',
};

function eventLabel(e, es) {
  const t = e.title ? `“${e.title}”` : '';
  switch (e.type) {
    case 'ARTICLE_PUBLISHED':
      return es ? `Penny publicó ${t}` : `Penny published ${t}`;
    case 'SOCIAL_PACKAGE_READY':
      return es ? `Emergent generó el paquete social ${t}` : `Emergent generated the social package ${t}`;
    case 'RSS_UPDATED':
      return es ? 'Feed RSS actualizado para distribución' : 'RSS feed refreshed for distribution';
    case 'POST_PUBLISHED':
      return es ? `Sonny publicó en ${e.source || 'redes'}` : `Sonny published to ${e.source || 'social'}`;
    case 'CAMPAIGN_CREATED':
      return es ? 'Sonny creó una campaña de distribución' : 'Sonny created a distribution campaign';
    case 'NEWSLETTER_SUBSCRIBED':
      return es ? 'Eva registró un nuevo suscriptor del newsletter' : 'Eva logged a new newsletter subscriber';
    case 'LEAD_CREATED':
      return es ? 'Stan registró un nuevo lead' : 'Stan logged a new lead';
    default:
      return e.type;
  }
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '';
  }
}

export default function AIActivityTimeline({ compact = false }) {
  const pulse = useOSPulse();
  const { lang } = useT();
  const es = lang === 'es';
  const events = (pulse?.events || []).slice(0, compact ? 6 : 12);
  if (!events.length) return null;

  return (
    <section className="bg-[#0B0D12] py-14 lg:py-18" data-testid="ai-activity-timeline">
      <div className={`${compact ? 'max-w-[1280px]' : 'max-w-[1180px]'} mx-auto px-6 lg:px-10`}>
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <p className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-[#FF9B4A]">
              <Activity className="w-3.5 h-3.5" /> AI Activity
            </p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-white tracking-[-0.015em]">
              {es ? 'El sistema, trabajando en tiempo real.' : 'The system, working in real time.'}
            </h2>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-400 shrink-0 mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
          </span>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-7">
          <div className="space-y-1">
            {events.map((e, i) => (
              <motion.div
                key={`${e.type}-${e.created_at}-${i}`}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(i * 0.05, 0.5) }}
                className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-white/[0.04] transition-colors"
                data-testid={`ai-activity-row-${i}`}
              >
                <span className="text-[11px] font-semibold text-neutral-500 tabular-nums w-11 shrink-0">
                  {fmtTime(e.created_at)}
                </span>
                <span className="inline-flex w-4 h-4 rounded-full bg-emerald-400/15 items-center justify-center shrink-0">
                  <Check className="w-2.5 h-2.5 text-emerald-400" />
                </span>
                <p className="text-[13px] text-neutral-300 min-w-0 truncate">
                  <span className="font-semibold text-white">{AGENT_BY_TYPE[e.type] || 'OS'}</span>
                  {' · '}
                  {e.slug ? (
                    <Link to={`/magazine/${e.slug}`} className="hover:text-[#FF9B4A] transition-colors">
                      {eventLabel(e, es)}
                    </Link>
                  ) : (
                    eventLabel(e, es)
                  )}
                </p>
              </motion.div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-white/[0.07] flex items-center justify-between gap-3">
            <p className="text-[10.5px] text-neutral-500">
              {es ? 'Eventos reales del Event Bus del AI Operating System.' : 'Real events from the AI Operating System event bus.'}
            </p>
            <Link
              to={compact ? '/intelligence' : '/os'}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#FF9B4A] hover:text-white transition-colors shrink-0"
              data-testid="ai-activity-link"
            >
              {compact
                ? (es ? 'Ver el Intelligence Hub' : 'See the Intelligence Hub')
                : (es ? 'Ver el OS en vivo' : 'Watch the OS live')}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
